/**
 * Audit Service - Phase 4 Architecture Migration
 *
 * Manages audit logging and retrieval for admin actions
 * Migrated to Phase 4 patterns: IService interface, ServiceResponse wrappers, typed Supabase client
 */

import { typedSupabase } from "@/lib/typed-supabase";
import { getMyceliumTransport } from "@flowstate/core";
import {
  IService,
  ServiceResponse,
} from "@/services/core/service-registry";
import { AuditLogsResponse } from "@/types/admin";
import { AuditLogEntry } from "./types";
import { logger } from "@/lib/logger";

export interface IAuditService extends IService {
  getAuditLogs(
    page?: number,
    limit?: number
  ): Promise<ServiceResponse<AuditLogsResponse>>;

  logAdminAction(params: {
    action: string;
    entity_type: string;
    entity_id?: string | undefined;
    details?: Record<string, unknown> | undefined;
  }): Promise<ServiceResponse<void>>;

  createAuditLog(params: {
    action: string;
    entity_type: string;
    entity_id?: string | undefined;
    details?: Record<string, unknown> | undefined;
  }): Promise<ServiceResponse<void>>;
}

export class AuditService implements IAuditService {
  readonly name = "audit" as const;
  private readonly supabase = typedSupabase;

  /**
   * Initialize the audit service
   */
  async initialize(): Promise<void> {
    logger.info("🔍 AuditService initialized");
  }

  /**
   * Dispose of the audit service
   */
  async dispose(): Promise<void> {
    logger.info("🔍 AuditService disposed");
  }

  /**
   * Get audit logs with pagination
   */
  async getAuditLogs(
    page = 0,
    limit = 50
  ): Promise<ServiceResponse<AuditLogsResponse>> {
    try {
      const start = page * limit;

      const { data: logs, error: logsError } = await this.supabase
        .from("audit_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .range(start, start + limit - 1);

      const { count, error: countError } = await this.supabase
        .from("audit_logs")
        .select("*", { count: "exact", head: true });

      if (logsError) {
        return { success: false, error: `Failed to fetch audit logs: ${logsError.message}` };
      }

      if (countError) {
        return { success: false, error: `Failed to count audit logs: ${countError.message}` };
      }

      const auditLogs: AuditLogsResponse = {
        logs: logs as AuditLogEntry[],
        count: count ?? 0,
      };

      return { success: true, data: auditLogs };
    } catch (error) {
      logger.error("Error fetching audit logs:", { error: error });
      return { success: false, error: error instanceof Error ? error.message : "Unknown error occurred" };
    }
  }

  /**
   * Log admin actions for audit trail
   */
  async logAdminAction({
    action,
    entity_type,
    entity_id = undefined,
    details = undefined,
  }: {
    action: string;
    entity_type: string;
    entity_id?: string | undefined;
    details?: Record<string, unknown> | undefined;
  }): Promise<ServiceResponse<void>> {
    try {
      const {
        data: { user },
      } = await this.supabase.auth.getUser();
      if (!user) {
        return { success: false, error: "User not authenticated" };
      }

      try {
        await getMyceliumTransport().publish({
          domain: 'system',
          type: action,
          userId: user.id,
          payload: {
            entityType: entity_type,
            entityId: entity_id,
            ...((details as Record<string, unknown>) || {})
          }
        });
      } catch (err) {
        return {
          success: false,
          error: `Failed to publish audit event: ${err instanceof Error ? err.message : 'Unknown error'}`,
        };
      }

      return { success: true, data: undefined };
    } catch (error) {
      logger.error("Error logging admin action:", { error: error });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  /**
   * Alias for logAdminAction for backward compatibility
   */
  async createAuditLog(params: {
    action: string;
    entity_type: string;
    entity_id?: string | undefined;
    details?: Record<string, unknown> | undefined;
  }): Promise<ServiceResponse<void>> {
    const response = await this.logAdminAction(params);
    if (!response.success) {
      return { success: false, error: response.error };
    }

    return { success: true, data: undefined };
  }
}

// Removed all legacy exports:
// export const auditService = new AuditService();
// export const getAuditLogs = (page = 0, limit = 50) =>
//   auditService.getAuditLogs(page, limit).then((res) => {
//     if (res.error) throw res.error;
//     return res.data!;
//   });
// export const logAdminAction = (
//   params: Parameters<AuditService["logAdminAction"]>[0]
// ) => auditService.logAdminAction(params);
// export const createAuditLog = logAdminAction;
// export default auditService;
